import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin, Clock, CheckCircle, Timer, XCircle, AlertTriangle,
  ShoppingCart, CreditCard, FileText, Share2, ChevronLeft, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import LoadingState from '@/components/shared/LoadingState';
import ErrorState from '@/components/shared/ErrorState';
import { Visit } from '@/api/localStore';

const STATUS_CONFIG = {
  completed: { label: 'مكتملة', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  active:    { label: 'قيد الزيارة', icon: Timer, color: 'text-blue-600', bg: 'bg-blue-50' },
  planned:   { label: 'مخططة', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
  cancelled: { label: 'ملغاة', icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
  missed:    { label: 'مفقودة', icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50' },
};

const OUTCOME_MAP = {
  order: { label: 'تم البيع', color: 'bg-blue-100 text-blue-700' },
  collection: { label: 'تحصيل', color: 'bg-emerald-100 text-emerald-700' },
  order_collection: { label: 'تم البيع والتحصيل', color: 'bg-indigo-100 text-indigo-700' },
  info_only: { label: 'متابعة', color: 'bg-amber-100 text-amber-700' },
  no_sale: { label: 'بدون نتيجة', color: 'bg-gray-100 text-gray-600' },
  issue: { label: 'مشكلة', color: 'bg-red-100 text-red-700' },
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' });
}

function InfoRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${highlight || 'text-foreground'}`}>{value}</span>
    </div>
  );
}

export default function VisitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Fetch the visit directly by id. The previous version listed up to 200
    // visits and matched with `v.id === id`, but useParams() returns the id as
    // a STRING while visit ids are numeric Odoo ids — so the strict comparison
    // never matched and every detail view showed "visit not found". Visit.get()
    // parseInt-coerces the id and fetches the single record from the backend,
    // returning the same normalized shape, so it also works on a fresh device
    // with an empty local cache.
    const found = await Visit.get(id);
    if (found) {
      setVisit(found);
    } else {
      setError('لم يتم العثور على الزيارة');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleShare = () => {
    if (!visit) return;
    const text = `تقرير زيارة\nالعميل: ${visit.customer_name}\nالتاريخ: ${fmtDateTime(visit.visit_date)}\nالمدة: ${visit.duration_minutes || 0} دقيقة\nالنتيجة: ${OUTCOME_MAP[visit.outcome]?.label || visit.outcome}`;
    if (navigator.share) navigator.share({ title: 'تقرير زيارة', text });
    else { navigator.clipboard.writeText(text); }
  };

  if (loading) return <div className="p-4"><LoadingState message="جاري تحميل الزيارة..." /></div>;
  if (error) return <div className="p-4"><ErrorState message={error} onRetry={load} /></div>;
  if (!visit) return null;

  const status = STATUS_CONFIG[visit.status] || STATUS_CONFIG.planned;
  const StatusIcon = status.icon;
  const outcome = OUTCOME_MAP[visit.outcome];

  const visitDate = new Date(visit.visit_date).toLocaleDateString('ar-EG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const checkInTime = fmtTime(visit.check_in_time || visit.visit_date);
  const checkOutTime = visit.check_out_time ? fmtTime(visit.check_out_time) : null;

  return (
    <div className="flex flex-col min-h-full pb-24" dir="rtl">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 flex items-center gap-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-xl hover:bg-muted">
          <ChevronLeft className="w-5 h-5 rtl-flip" />
        </button>
        <h1 className="font-bold text-foreground flex-1">تفاصيل الزيارة</h1>
        <div className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${status.bg} ${status.color}`}>
          <StatusIcon className="w-3.5 h-3.5" />
          {status.label}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Hero card */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          {/* Gradient top */}
          <div className="bg-gradient-to-br from-purple-600 to-purple-400 px-4 pt-4 pb-6 text-white">
            <p className="text-white/70 text-xs mb-1">{visitDate}</p>
            <h2 className="text-xl font-black">{visit.customer_name}</h2>
            {visit.location_address && (
              <p className="flex items-center gap-1 text-white/80 text-sm mt-1">
                <MapPin className="w-3.5 h-3.5" />{visit.location_address}
              </p>
            )}
          </div>

          <div className="p-4 -mt-3 bg-card rounded-t-2xl">
            <div className="grid grid-cols-3 gap-3 mb-3">
              {[
                { label: 'دخول', val: checkInTime },
                { label: 'خروج', val: checkOutTime || '—' },
                { label: 'المدة', val: visit.duration_minutes ? `${visit.duration_minutes} دق` : '—' },
              ].map((s, i) => (
                <div key={i} className="text-center bg-muted/40 rounded-xl p-2">
                  <p className="font-black text-foreground">{s.val}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {outcome && (
                <span className={`text-xs px-3 py-1 rounded-full font-bold ${outcome.color}`}>{outcome.label}</span>
              )}
              {visit.gps_verified && (
                <span className="text-xs px-3 py-1 rounded-full font-bold bg-emerald-100 text-emerald-700">📍 GPS موثق ✓</span>
              )}
              {visit.visit_score > 0 && (
                <span className={`text-xs px-3 py-1 rounded-full font-bold ${visit.visit_score >= 70 ? 'bg-emerald-100 text-emerald-700' : visit.visit_score >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                  نقاط: {visit.visit_score}/100
                </span>
              )}
            </div>
          </div>
        </motion.div>

        {/* Details */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="px-4 py-2.5 bg-muted/30 border-b border-border">
            <p className="text-xs font-bold text-muted-foreground">معلومات الزيارة</p>
          </div>
          <div className="px-4">
            <InfoRow label="المندوب" value={visit.salesman_name || '—'} />
            <InfoRow label="تاريخ الزيارة" value={visitDate} />
            {visit.gps_accuracy_meters && (
              <InfoRow label="دقة GPS" value={`${visit.gps_accuracy_meters} متر`} />
            )}
            {visit.distance_to_customer_meters > 0 && (
              <InfoRow label="المسافة من العميل" value={`${visit.distance_to_customer_meters} متر`} highlight={visit.distance_to_customer_meters < 100 ? 'text-emerald-600' : 'text-amber-600'} />
            )}
            {visit.no_sale_reason && (
              <InfoRow label="سبب عدم البيع" value={visit.no_sale_reason} highlight="text-red-500" />
            )}
          </div>
        </div>

        {/* GPS coordinates + arrival address */}
        {(visit.latitude || visit.checkout_latitude || visit.location_address) && (
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-4 py-2.5 bg-muted/30 border-b border-border flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <p className="text-xs font-bold text-muted-foreground">الموقع</p>
            </div>
            <div className="px-4">
              {visit.location_address && (
                <InfoRow label="العنوان" value={visit.location_address} />
              )}
              {visit.latitude ? (
                <a
                  href={`https://www.google.com/maps?q=${visit.latitude},${visit.longitude}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex justify-between items-center py-2.5 border-b border-border last:border-0 active:opacity-70">
                  <span className="text-sm text-muted-foreground">موقع الوصول</span>
                  <span className="text-sm font-semibold text-primary flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> افتح على الخريطة
                  </span>
                </a>
              ) : null}
              {visit.checkout_latitude ? (
                <a
                  href={`https://www.google.com/maps?q=${visit.checkout_latitude},${visit.checkout_longitude}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex justify-between items-center py-2.5 border-b border-border last:border-0 active:opacity-70">
                  <span className="text-sm text-muted-foreground">موقع المغادرة</span>
                  <span className="text-sm font-semibold text-primary flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> افتح على الخريطة
                  </span>
                </a>
              ) : null}
            </div>
          </div>
        )}

        {/* Notes */}
        {visit.notes && (
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <p className="text-xs font-bold text-muted-foreground">ملاحظات</p>
            </div>
            <p className="text-sm text-foreground leading-relaxed">{visit.notes}</p>
          </div>
        )}

        {/* Linked activities */}
        {(visit.related_order_id || visit.related_collection_id) && (
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-4 py-2.5 bg-muted/30 border-b border-border">
              <p className="text-xs font-bold text-muted-foreground">الأنشطة المرتبطة</p>
            </div>
            <div className="divide-y divide-border">
              {visit.related_order_id && (
                <button onClick={() => navigate(`/orders/${visit.related_order_id}`)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">عرض الطلب المرتبط</span>
                  <ChevronLeft className="w-4 h-4 text-muted-foreground mr-auto rtl-flip" />
                </button>
              )}
              {visit.related_collection_id && (
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">تحصيل مرتبط</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick actions to start new order/collection for this customer */}
        <div className="grid grid-cols-2 gap-3">
          <Button onClick={() => navigate(`/orders/new?customer_id=${visit.customer_id}&customer_name=${encodeURIComponent(visit.customer_name)}`)}
            variant="outline" className="rounded-2xl h-12 gap-2 text-sm border-blue-200 text-blue-700 hover:bg-blue-50">
            <ShoppingCart className="w-4 h-4" /> طلب جديد
          </Button>
          <Button onClick={() => navigate(`/collections/new?customer_id=${visit.customer_id}&customer_name=${encodeURIComponent(visit.customer_name)}`)}
            variant="outline" className="rounded-2xl h-12 gap-2 text-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50">
            <CreditCard className="w-4 h-4" /> تحصيل جديد
          </Button>
        </div>
      </div>

      {/* Action bar */}
      <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border px-4 py-3 flex gap-2 z-20">
        <Button onClick={handleShare} variant="outline" className="flex-1 rounded-xl h-11 gap-1.5">
          <Share2 className="w-4 h-4" /> مشاركة
        </Button>
        <Button onClick={() => navigate(`/visits/new?customer_id=${visit.customer_id}&customer_name=${encodeURIComponent(visit.customer_name)}`)}
          className="flex-1 bg-purple-600 text-white rounded-xl h-11 gap-1.5">
          <MapPin className="w-4 h-4" /> زيارة جديدة
        </Button>
      </div>
    </div>
  );
}